import { ReactNode } from "react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

import Modal, { ModalControlProps } from "../../layouts/Modal";
import LoadingWheel from "../shared/LoadingWheel";
import useCoreDeploymentRun from "../../data/CoreDeployment/useCoreDeploymentRun";
import {
  ICoreDeploymentTarget,
  parseWorkflowSummary,
} from "../../models/CoreDeployment/CoreDeploymentRun";
import {
  CoreDeploymentStatusPill,
  CoreDeploymentTargetStatusPill,
  CoreTargetBranchPill,
  formatDateTime,
  shortSha,
  workflowConclusionClass,
} from "./coreDeploymentMeta";
import { CoreDeploymentOverview } from "./CoreDeploymentOverview";

interface ViewCoreDeploymentModalProps extends ModalControlProps {
  runId?: string | null;
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="text-sm text-gray-900 mt-0.5">{children}</dd>
    </div>
  );
}

function TargetRow({ target }: { target: ICoreDeploymentTarget }) {
  const workflows = parseWorkflowSummary(target.workflowSummary);

  return (
    <li className="rounded-lg border border-gray-200 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-900">{target.repo}</span>
        <span className="text-gray-400">→</span>
        <CoreTargetBranchPill branch={target.targetBranch} />
        <CoreDeploymentTargetStatusPill status={target.status} />
        <span className="flex-1" />
        {target.pullRequestUrl && (
          <a
            href={target.pullRequestUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-orange-700 hover:underline"
          >
            PR #{target.pullRequestNumber}
            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          </a>
        )}
      </div>

      <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 md:grid-cols-3">
        <Field label="Merge commit">
          <span className="font-mono">{shortSha(target.mergeSha)}</span>
        </Field>
        <Field label="Merged at">{formatDateTime(target.mergedAt)}</Field>
        <Field label="Message">{target.message || "-"}</Field>
      </dl>

      {workflows.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-gray-100 pt-2">
          {workflows.map((workflow) => (
            <li key={workflow.id} className="flex items-center gap-2 text-sm">
              <span className={workflowConclusionClass(workflow.status, workflow.conclusion)}>
                {workflow.status === "completed" ? workflow.conclusion ?? "unknown" : workflow.status}
              </span>
              <a
                href={workflow.html_url}
                target="_blank"
                rel="noreferrer"
                className="text-gray-800 hover:underline truncate"
                title={workflow.path}
              >
                {workflow.name}
              </a>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function ViewCoreDeploymentModal({ open, setOpen, runId }: ViewCoreDeploymentModalProps) {
  const { run } = useCoreDeploymentRun(open ? runId : null);
  const data = run.data;

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-5xl">
      <div className="border-b pb-3 flex items-center gap-3">
        <h2 className="text-lg font-stick font-medium leading-6 text-gray-900">Core Deployment</h2>
        {data && <CoreDeploymentStatusPill status={data.status} />}
      </div>

      {run.isLoading || !data ? (
        <div className="py-10">
          <LoadingWheel />
        </div>
      ) : (
        <div className="mt-4 space-y-4">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3 md:grid-cols-3">
            <Field label="Scheduled for">{formatDateTime(data.scheduledFor)}</Field>
            <Field label="Started">{formatDateTime(data.startedAt)}</Field>
            <Field label="Completed">{formatDateTime(data.completedAt)}</Field>
            <Field label="Requested by">{data.requestedByName || data.requestedByEmail}</Field>
            <Field label="Targets">
              <span className="flex gap-1">
                {data.deployCore && <CoreTargetBranchPill branch="core-deployment" />}
                {data.deployTme && <CoreTargetBranchPill branch="tme-deployment" />}
              </span>
            </Field>
            <Field label="Staging verified at">
              <span className="font-mono">
                API {shortSha(data.stagingApiSha)} · Dashboard {shortSha(data.stagingDashboardSha)}
              </span>
            </Field>
            {data.cancelledByEmail && (
              <Field label="Cancelled by">
                {data.cancelledByEmail} ({formatDateTime(data.cancelledAt)})
              </Field>
            )}
            {data.notes && (
              <div className="col-span-2 md:col-span-3">
                <Field label="Notes">
                  <span className="whitespace-pre-line">{data.notes}</span>
                </Field>
              </div>
            )}
          </dl>

          {data.failureReason && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 whitespace-pre-line">
              {data.failureReason}
            </div>
          )}

          <div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Targets</h3>
            {data.targets.length === 0 ? (
              <p className="text-sm text-gray-500">No targets recorded.</p>
            ) : (
              <ul className="space-y-2">
                {data.targets.map((target) => (
                  <TargetRow key={target.id} target={target} />
                ))}
              </ul>
            )}
          </div>

          {(data.status === "Succeeded" || data.status === "Failed") && (
            <div className="border-t pt-4">
              <CoreDeploymentOverview runId={data.id} />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
